import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { Loader2, LogOut, FileText, Clock, Award, Calendar, TrendingUp, Play, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { useStudentAuth } from "@/lib/studentAuth";
import { formatApiErrorDetail } from "@/lib/auth";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(s) {
  try {
    return new Date(s).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return s; }
}

export default function StudentDashboard() {
  const { student, authHeader, logout } = useStudentAuth();
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [examsRes, resultsRes] = await Promise.all([
        axios.get(`${API}/exams`),
        axios.get(`${API}/student/my-results`, { headers: authHeader }),
      ]);
      setExams(examsRes.data || []);
      setResults(resultsRes.data || []);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Could not load dashboard";
      toast.error(msg);
      if (err.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [authHeader, logout]);

  useEffect(() => { if (student) load(); }, [student, load]);

  if (student === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]"><Loader2 className="w-8 h-8 animate-spin text-[#002570]" /></div>;
  }
  if (student === null) return <Navigate to="/student/login" replace />;

  const onLogout = () => { logout(); toast.success("Signed out"); navigate("/student/login", { replace: true }); };

  const best = results.length ? Math.max(...results.map((r) => r.percentage || 0)) : 0;
  const latest = results[0];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-3" data-testid="student-logo-link">
            <img src={SITE.logo.horizontal} alt={SITE.name} className="h-9 md:h-10 w-auto object-contain" />
          </a>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline-flex font-inter text-sm text-slate-600" data-testid="student-name-label">
              <GraduationCap className="w-4 h-4 mr-2 inline text-[#002570]" />
              {student.name} · {student.username}
            </span>
            <button
              onClick={onLogout}
              data-testid="student-logout-btn"
              className="inline-flex items-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] rounded-full px-4 md:px-5 py-2.5 text-sm font-inter font-medium transition-colors"
            >
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="mb-8">
          <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#002570]">Student Dashboard</p>
          <h1 className="mt-2 font-rubik text-3xl md:text-4xl font-bold text-[#0f172a]">Welcome, {student.name.split(" ")[0]} 👋</h1>
          <p className="mt-2 font-inter text-sm text-slate-600">Take a mock, review your past attempts and track your improvement.</p>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {[
            { icon: FileText, label: "Attempts", value: results.length, accent: "#002570" },
            { icon: Award, label: "Best Score", value: results.length ? `${best.toFixed(1)}%` : "—", accent: "#73c3ff" },
            { icon: TrendingUp, label: "Last Score", value: latest ? `${latest.percentage.toFixed(1)}%` : "—", accent: "#002570" },
          ].map(({ icon: Icon, label, value, accent }) => (
            <div key={label} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex items-center gap-4" data-testid={`student-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${accent}15`, color: accent }}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
                <p className="font-rubik text-2xl font-bold text-[#0f172a]">{value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Exams list */}
        <section className="mb-12">
          <h2 className="font-rubik text-xl md:text-2xl font-bold text-[#0f172a] mb-4">Available Mock Exams</h2>
          {loading ? (
            <div className="bg-white rounded-2xl p-10 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-3" /> Loading…</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {exams.map((exam) => (
                <div key={exam.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex flex-col" data-testid={`exam-card-${exam.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <span className="inline-block bg-[#73c3ff]/15 text-[#002570] text-xs font-inter font-semibold px-3 py-1 rounded-full">{exam.course}</span>
                      <h3 className="mt-3 font-rubik text-lg font-bold text-[#0f172a]">{exam.title}</h3>
                    </div>
                    <div className="text-right">
                      <p className="font-rubik text-2xl font-bold text-[#002570]">{exam.max_score}</p>
                      <p className="font-inter text-[10px] uppercase tracking-wider text-slate-400">marks</p>
                    </div>
                  </div>
                  <p className="font-inter text-sm text-slate-600 mb-4">{exam.description}</p>
                  <div className="flex items-center gap-4 mb-5 font-inter text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {Math.round(exam.duration_minutes / 60)}h {exam.duration_minutes % 60 ? `${exam.duration_minutes % 60}m` : ""}</span>
                  </div>
                  <Link
                    to={`/student/exam/${exam.id}`}
                    data-testid={`start-exam-${exam.id}-btn`}
                    className="mt-auto inline-flex items-center justify-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] rounded-full px-6 py-3 font-inter font-medium transition-all"
                  >
                    <Play className="w-4 h-4" /> Start Mock Exam
                  </Link>
                </div>
              ))}
              {exams.length === 0 && (
                <div className="bg-white rounded-2xl p-10 text-center text-slate-500 col-span-full">No exams available yet.</div>
              )}
            </div>
          )}
        </section>

        {/* Past attempts */}
        <section>
          <h2 className="font-rubik text-xl md:text-2xl font-bold text-[#0f172a] mb-4">Your Past Attempts</h2>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading…</div>
            ) : results.length === 0 ? (
              <div className="text-center py-16 px-6">
                <div className="mx-auto w-14 h-14 rounded-full bg-[#73c3ff]/15 text-[#002570] flex items-center justify-center mb-4"><Award className="w-6 h-6" /></div>
                <p className="font-rubik text-lg font-semibold text-[#0f172a]">No attempts yet</p>
                <p className="mt-1 font-inter text-sm text-slate-500">Take your first mock exam to see your performance here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="student-results-table">
                  <thead className="bg-[#f8fafc] border-b border-slate-200">
                    <tr>
                      {["Exam", "Score", "%", "MCQ / Case", "Time", "Date"].map((h) => (
                        <th key={h} className="text-left font-inter text-xs font-semibold uppercase tracking-wider text-slate-500 px-4 md:px-5 py-4 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r) => (
                      <tr key={r.id} data-testid={`result-row-${r.id}`} className="border-b border-slate-100 hover:bg-[#f8fafc] transition-colors">
                        <td className="px-4 md:px-5 py-4 font-rubik font-semibold text-[#0f172a]">{r.exam_title}</td>
                        <td className="px-4 md:px-5 py-4 font-rubik text-base font-bold text-[#002570]">{r.score} / {r.max_score}</td>
                        <td className="px-4 md:px-5 py-4">
                          <span className={`inline-block text-xs font-inter font-semibold px-3 py-1.5 rounded-full ${r.percentage >= 60 ? "bg-emerald-50 text-emerald-700" : r.percentage >= 40 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>{r.percentage.toFixed(1)}%</span>
                        </td>
                        <td className="px-4 md:px-5 py-4 font-inter text-sm text-slate-600 whitespace-nowrap">{r.mcq_marks}/400 · {r.case_marks}/100</td>
                        <td className="px-4 md:px-5 py-4 font-inter text-xs text-slate-500 whitespace-nowrap">{r.time_used || "—"}{r.auto_submitted ? " (auto)" : ""}</td>
                        <td className="px-4 md:px-5 py-4 font-inter text-xs text-slate-500 whitespace-nowrap"><Calendar className="w-3 h-3 inline mr-1" />{formatDate(r.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
